import React, { useEffect, useMemo, useState } from "react";
import {
  assignApprover,
  fetchIndividualApproverSubmission,
  getReviewColumns,
  submitReviewedSubmission,
} from "../../../services/api";
import { updateReviewColumnData } from "../../../redux/slice/brainSlice";
import { useDispatch } from "react-redux";
import APP_CONSTANTS from "../../../utils/config";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "@mui/styles";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import CircularProgress from "@mui/material/CircularProgress";
import {
  Close as CloseIcon,
  Check as CheckIcon,
  Block as BlockIcon,
  Edit as EditIcon,
  DoneAll as DoneAllIcon,
  LockOutlined as LockOutlinedIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  InboxOutlined as InboxOutlinedIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";
import { brainStyles } from "../../../theme/style";
import {
  getLocalStorageData,
  isRestrictedKey,
  isTagKey,
  parseTagValue,
  setLocalStorageData,
  valueToString,
} from "../components/reviewColumnUtils";
import TagInput from "../components/TagInput";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { updateAlertInfo } from "../../../redux/slice/userSlice";
import StatusChip from "../components/StatusChip";
import ColumnVisibilityControl from "../components/ColumnVisibilityControl";
import SKUDetailsDrawer from "../components/SKUDetailsDrawer";
import Loader from "../../../components/Loader";
import Error from "../../../components/Error/Error";

const ROW_PENDING = "pending";
const ROW_APPROVED = "approved";
const ROW_REJECTED = "rejected";

function useTableColumns(columnData) {
  const [visibleColumns, setVisibleColumns] = useState(new Set([]));
  const [columnOrder, setColumnOrder] = useState([]);
  useEffect(() => {
    if (!columnData?.columns) return;
    const all_columns = columnData.columns;
    const all_column_list = all_columns.map((c) => c.key);
    const user_editable_columns = all_columns
      .filter((c) => c.user_edit_access)
      .map((c) => c.key);
    let visibleColumn_data =
      getLocalStorageData(APP_CONSTANTS.VISIBLE_COLUMN) || [];
    let columnOrderData = getLocalStorageData(APP_CONSTANTS.COLUMN_ORDER) || [];
    const isSame =
      all_column_list.length === columnOrderData.length &&
      all_column_list.every((item) => columnOrderData.includes(item));
    const isIncluded = user_editable_columns.every((item) =>
      visibleColumn_data.includes(item)
    );
    if (
      !visibleColumn_data?.length ||
      !columnOrderData?.length ||
      !isSame ||
      !isIncluded
    ) {
      const non_editable_columns = all_columns
        .filter((c) => c.is_editable_column === false)
        .map((c) => c.key);
      const user_non_editable_columns = all_columns
        .filter((c) => c.is_editable_column && !c.user_edit_access)
        .map((c) => c.key);
      visibleColumn_data = [
        ...(columnData.key_reference_columns || []),
        ...user_editable_columns,
      ];
      columnOrderData = [
        ...non_editable_columns,
        ...user_editable_columns,
        ...user_non_editable_columns,
      ];
      setLocalStorageData(APP_CONSTANTS.VISIBLE_COLUMN, visibleColumn_data);
      setLocalStorageData(APP_CONSTANTS.COLUMN_ORDER, columnOrderData);
    }
    if (visibleColumns.size === 0 || columnOrder.length === 0) {
      setVisibleColumns(new Set(visibleColumn_data));
      setColumnOrder(columnOrderData);
    }
  }, [columnData]);
  return { visibleColumns, setVisibleColumns, columnOrder, setColumnOrder };
}

function useReviewState(rows = []) {
  const [reviewedRows, setReviewedRows] = useState([]);
  const rowCategorization = useMemo(() => {
    const approvedRowIds = new Set(
      reviewedRows.filter((r) => r.status === ROW_APPROVED).map((r) => r.rowId)
    );
    const rejectedRowIds = new Set(
      reviewedRows.filter((r) => r.status === ROW_REJECTED).map((r) => r.rowId)
    );
    const categorized = { all: rows, pending: [], approved: [], rejected: [] };
    rows.forEach((row) => {
      if (approvedRowIds.has(row.rowId)) {
        categorized.approved.push(row);
      } else if (rejectedRowIds.has(row.rowId)) {
        categorized.rejected.push(row);
      } else {
        categorized.pending.push(row);
      }
    });
    const counts = {
      all: categorized.all.length,
      pending: categorized.pending.length,
      approved: categorized.approved.length,
      rejected: categorized.rejected.length,
    };
    return { visibleRows: categorized, counts };
  }, [rows, reviewedRows]);

  useEffect(() => {
    const preRejectedRows = [];
    const preApprovedRows = [];
    rows.map((item, _) => {
      if (item.status === "approved") {
        preApprovedRows.push({
          rowId: item.rowId,
          status: item.status,
          comment: "",
        });
      } else if (item.status === "rejected") {
        preRejectedRows.push({
          rowId: item.rowId,
          status: item.status,
          comment: "",
        });
      }
    });

    setReviewedRows([...preApprovedRows, ...preRejectedRows]);
  }, [rows]);
  const handleApprove = (rowId) => {
    if (!reviewedRows.some((item) => item.rowId === rowId)) {
      setReviewedRows((prev) => [
        ...prev,
        { rowId, status: ROW_APPROVED, comment: "" },
      ]);
    }
  };
  const handleReset = (rowId) => {
    setReviewedRows((prev) => prev.filter((item) => item.rowId !== rowId));
  };
  const rejectRow = (rowId, recentComment) => {
    setReviewedRows((prev) => {
      const isPresent = prev.some((item) => item.rowId === rowId);
      if (isPresent) {
        return prev.map((item) =>
          item.rowId === rowId
            ? { ...item, status: ROW_REJECTED, comment: recentComment }
            : item
        );
      }
      return [...prev, { rowId, status: ROW_REJECTED, comment: recentComment }];
    });
  };
  const approveRemaining = () => {
    const newlyApproved = rowCategorization.visibleRows.pending.map((item) => ({
      rowId: item.rowId,
      status: ROW_APPROVED,
      comment: "",
    }));
    setReviewedRows((prev) => [...prev, ...newlyApproved]);
  };
  return {
    reviewedRows,
    visibleRows: rowCategorization.visibleRows,
    counts: rowCategorization.counts,
    handleApprove,
    handleReset,
    rejectRow,
    approveRemaining,
  };
}

function ColumnHeader({ label, info, isRestricted, theme }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.4,
        userSelect: "none",
      }}
    >
      <Box
        component="span"
        sx={{
          cursor: "default",
          color: isRestricted
            ? theme.palette.brain.table.header.restrictedText
            : "inherit",
        }}
      >
        {label}
      </Box>
      {isRestricted && (
        <LockOutlinedIcon
          sx={{
            fontSize: 11,
            color: theme.palette.brain.table.header.restrictedText,
          }}
        />
      )}
      <Tooltip title={info} arrow placement="top">
        <InfoOutlinedIcon
          sx={{
            fontSize: 13,
            color: theme.palette.brain.table.header.restrictedText,
            cursor: "help",
          }}
        />
      </Tooltip>
    </Box>
  );
}

function RenderTooltip({
  theme,
  prevRowComment,
  recentComment,
  rowId,
  setOpenRejectModal,
}) {
  return (
    <Box
      sx={{
        width: 420,
        borderRadius: "14px",
        overflow: "hidden",
        backgroundColor: "#ffffff",
        border: "1px solid #e2e8f0",
        boxShadow: "0 10px 30px rgba(15, 23, 42, 0.12)",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.3,
          borderBottom: "1px solid #e2e8f0",
          background: "linear-gradient(to right, #f8fafc, #f1f5f9)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            fontSize: "0.78rem",
            fontWeight: 700,
            color: "#0f172a",
            textTransform: "uppercase",
          }}
        >
          Review Comments
        </Box>
        <ChatBubbleOutlineIcon sx={{ fontSize: 16, color: "#64748b" }} />
      </Box>
      {prevRowComment && (
        <Box
          sx={{
            px: 2,
            py: 1.6,
            borderBottom: "1px solid #f1f5f9",
            backgroundColor: "#fffaf3",
          }}
        >
          <Box
            sx={{
              fontSize: "0.7rem",
              fontWeight: 700,
              color: "#b45309",
              mb: 0.7,
              textTransform: "uppercase",
            }}
          >
            Last Comment
          </Box>
          <Box
            sx={{
              fontSize: "0.82rem",
              color: "#334155",
              lineHeight: 1.7,
              whiteSpace: "pre-wrap",
            }}
          >
            {prevRowComment || "No last comment available."}
          </Box>
        </Box>
      )}
      {recentComment && (
        <Box sx={{ px: 2, py: 1.6, backgroundColor: "#f8fafc" }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 0.7 }}>
            <Box
              sx={{
                fontSize: "0.7rem",
                fontWeight: 700,
                color: "#0f766e",
                textTransform: "uppercase",
              }}
            >
              Recent Comment
            </Box>
            <EditIcon
              onClick={() => {
                setOpenRejectModal({
                  open: true,
                  data: { prevComment: prevRowComment, rowId, recentComment },
                });
              }}
              sx={{
                color: theme.palette.secondary.main2,
                fontSize: "16px",
                cursor: "pointer",
              }}
            />
          </Box>
          <Box
            sx={{
              fontSize: "0.82rem",
              color: "#334155",
              lineHeight: 1.7,
              whiteSpace: "pre-wrap",
            }}
          >
            {recentComment || "No recent comment added."}
          </Box>
        </Box>
      )}
    </Box>
  );
}

const ReviewTableRow = React.memo(
  ({
    row,
    vIdx,
    theme,
    classes,
    readOnly,
    rowStatus,
    prevRowComment,
    recentComment,
    displayedColumns,
    columnData,
    handleApprove,
    handleRejectRow,
    handleReset,
    renderCellValue,
    setOpenRejectModal,
    skuCount = 0,
    skuList,
    handleSkuClick = () => {},
    preApprovedRowIds = [],
  }) => {
    const rowBg =
      vIdx % 2 === 0
        ? theme.palette.brain.drawer.item_background
        : theme.palette.brain.table.row.background;
    const changedMap = useMemo(
      () => new Map(row.changes?.map((c) => [c.column, c]) || []),
      [row.changes]
    );
    return (
      <TableRow
        sx={{ backgroundColor: rowBg, "& > td": { backgroundColor: rowBg } }}
      >
        <TableCell
          className={classes.baseCell}
          sx={{
            position: "sticky",
            left: 0,
            zIndex: 2,
            borderRight: `1px solid ${theme.palette.brain.grey.border}`,
            minWidth: 195,
          }}
        >
          {/* Status & SKU Record */}
          <Box
            sx={{
              display: "flex",
              width: "100%",
              marginBottom: "10px",
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
              <Tooltip title={rowStatus} arrow>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor:
                      rowStatus === "approved"
                        ? theme.palette.brain.green.text3
                        : rowStatus === "rejected"
                        ? theme.palette.brain.red.text
                        : theme.palette.brain.yellow.text,
                  }}
                />
              </Tooltip>
              {(readOnly || preApprovedRowIds.includes(row.rowId)) && (
                <Tooltip
                  title={
                    preApprovedRowIds.includes(row.rowId)
                      ? "Previously approved — row is frozen."
                      : "Read only"
                  }
                  arrow
                >
                  <LockOutlinedIcon
                    sx={{
                      fontSize: 16,
                      color: theme.palette.brain.table.row.text,
                    }}
                  />
                </Tooltip>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                flexWrap: "nowrap",
                flex: 1,
                justifyContent: "space-between",
              }}
            >
              {skuList?.length === 0 ? (
                "-"
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flex: 1,
                  }}
                >
                  <TagInput
                    value={skuList}
                    disabled={true}
                    onChange={() => {}}
                    maxVisible={1}
                    tagLabel={"All SKUs"}
                  />
                  <Box>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSkuClick(
                          row.snapshot.upc12,
                          row.snapshot.description,
                          row.rowId
                        );
                      }}
                      size="small"
                    >
                      <VisibilityOutlinedIcon
                        sx={{
                          fontSize: "16px",
                          color: theme.palette.secondary.main2,
                        }}
                      />
                    </IconButton>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          {/* Comment & Action */}
          <Box
            sx={{
              display: "flex",
              width: "100%",
            }}
          >
            {!preApprovedRowIds.includes(row.rowId) &&
              (prevRowComment || recentComment) && (
                <Tooltip
                  arrow
                  placement="right"
                  slotProps={{
                    popper: { sx: { zIndex: 10005 } },
                    tooltip: {
                      sx: {
                        background: "transparent",
                        p: 0,
                        boxShadow: "none",
                        // maxWidth: 460,
                      },
                    },
                  }}
                  title={
                    <RenderTooltip
                      theme={theme}
                      prevRowComment={prevRowComment}
                      recentComment={recentComment}
                      rowId={row.rowId}
                      setOpenRejectModal={setOpenRejectModal}
                    />
                  }
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.25,
                      color: theme.palette.brain.rejected.primaryText,
                      cursor: "pointer",
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 0.5,
                      bgcolor: theme.palette.brain.rejected.background,
                      border: `1px solid ${theme.palette.brain.rejected.border}`,
                    }}
                  >
                    <ChatBubbleOutlineIcon sx={{ fontSize: 12 }} /> Comment
                  </Box>
                </Tooltip>
              )}
            {!(readOnly || preApprovedRowIds.includes(row.rowId)) && (
              <Box sx={{ display: "flex", gap: 0.5, marginLeft: "auto" }}>
                <>
                  <Tooltip title="Approve this row">
                    <IconButton
                      size="small"
                      onClick={() => handleApprove(row.rowId)}
                      sx={{
                        p: 0.25,
                        color:
                          rowStatus === ROW_APPROVED
                            ? "#fff"
                            : theme.palette.brain.green.text2,
                        bgcolor:
                          rowStatus === ROW_APPROVED
                            ? theme.palette.brain.green.text2
                            : "transparent",
                        border: `1px solid ${theme.palette.brain.green.text2}`,
                        borderRadius: 1,
                      }}
                    >
                      <CheckIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Reject this row (reason required)">
                    <IconButton
                      size="small"
                      onClick={() => handleRejectRow(row.rowId, prevRowComment)}
                      sx={{
                        p: 0.25,
                        color:
                          rowStatus === ROW_REJECTED
                            ? "#fff"
                            : theme.palette.brain.red.text,
                        bgcolor:
                          rowStatus === ROW_REJECTED
                            ? theme.palette.brain.red.text
                            : "transparent",
                        border: `1px solid ${theme.palette.brain.red.text}`,
                        borderRadius: 1,
                      }}
                    >
                      <BlockIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                  {rowStatus !== ROW_PENDING && (
                    <Tooltip title="Clear decision">
                      <IconButton
                        size="small"
                        onClick={() => handleReset(row.rowId)}
                        sx={{
                          p: 0.25,
                          color: theme.palette.secondary.main2,
                          border: `1px solid ${theme.palette.secondary.main2}`,
                          borderRadius: 1,
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              </Box>
            )}
          </Box>
        </TableCell>

        {displayedColumns.map((col) => {
          const isRestricted = isRestrictedKey(
            col.key,
            columnData?.restricted_columns
          );
          const change = changedMap.get(col.key);
          const isChanged = !!change;
          return (
            <TableCell
              key={col.key}
              className={classes.baseCell}
              sx={{
                bgcolor: isRestricted
                  ? "rgba(148, 163, 184, 0.05)"
                  : change?.editedValue
                  ? theme.palette.brain.table.row.editedBackground
                  : undefined,
                borderLeft: isChanged
                  ? `3px solid ${theme.palette.brain.table.row.editedBorder}`
                  : "3px solid transparent",
                color:
                  isRestricted && !isChanged
                    ? theme.palette.brain.table.header.restrictedText
                    : theme.palette.brain.table.row.text,
                cursor: isRestricted ? "not-allowed" : "default",
                fontWeight: 400,
                whiteSpace: "nowrap",
              }}
            >
              {renderCellValue(row, col.key, isChanged, change)}
            </TableCell>
          );
        })}
      </TableRow>
    );
  }
);

function RejectModal({
  open,
  data,
  theme,
  onClose = () => {},
  onSave = () => {},
}) {
  const [value, setValue] = useState("");
  useEffect(() => {
    if (open) {
      setValue(data?.recentComment || "");
    } else {
      setValue("");
    }
  }, [open, data?.recentComment]);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{ zIndex: 10006 }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          background: theme.palette.brain.table.tableBackground,
        },
      }}
    >
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}
      >
        <BlockIcon sx={{ color: theme.palette.brain.red.text }} />
        <Box>
          <Typography
            component="span"
            sx={{
              fontWeight: 700,
              fontSize: "1rem",
              color: theme.palette.secondary.main2,
            }}
          >
            Reject row {data?.rowId !== null ? `#${data?.rowId}` : ""}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block" }}
          >
            Provide a reason — this will be visible to the reviewer.
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {data?.prevComment && (
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1.5,
              border: `1px solid ${theme.palette.brain.yellow.border}`,
              bgcolor: theme.palette.brain.yellow.background,
              mb: 2,
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 0.75 }}
            >
              <ChatBubbleOutlineIcon
                sx={{ fontSize: 16, color: theme.palette.brain.yellow.text }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.brain.yellow.text,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                }}
              >
                Last review
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              sx={{
                mt: 0.25,
                color: theme.palette.brain.table.row.text,
                whiteSpace: "pre-wrap",
                fontSize: "0.8rem",
              }}
            >
              {data?.prevComment}
            </Typography>
          </Box>
        )}
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.brain.table.row.text,
            fontSize: "0.82rem",
            fontWeight: 600,
          }}
        >
          Overall Comment
        </Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={4}
          maxRows={8}
          placeholder="Why is this row being rejected?"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          sx={{
            mt: 1,
            "& .MuiInputBase-input": {
              fontSize: "0.78rem",
              py: "5px",
              px: "8px",
              color: theme.palette.brain.table.row.text,
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.brain.mis.grey.background1,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.secondary.main2,
            },
            backgroundColor: theme.palette.brain.table.background1,
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            borderColor: theme.palette.secondary.main2,
            color: theme.palette.secondary.main2,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => onSave(data?.rowId, value)}
          variant="contained"
          color="error"
        >
          Save & Reject
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ApproverDetailPage() {
  const dispatch = useDispatch();
  const columnData = useSelector((state) => state.brain.columnData);
  const param = useParams();
  const submissionId = param?.id;
  const [selectedBatch, setSelectedBatch] = useState({
    status: "initial",
    data: {},
    error: "",
  });

  const [skuDrawer, setSkuDrawer] = useState({
    open: false,
    data: {},
  });

  const [processing, setProcessing] = useState(false);

  const theme = useTheme();
  const classes = brainStyles(theme);
  const navigate = useNavigate();
  const [tab, setTab] = useState("all");
  const [openRejectModal, setOpenRejectModal] = useState({
    open: false,
    data: {},
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [overallReason, setOverallReason] = useState("");
  const user_email = useSelector((state) => state.auth.metadata.email);
  const [preApprovedRowIds, setPreApprovedRowIds] = useState([]);

  const { visibleColumns, setVisibleColumns, columnOrder, setColumnOrder } =
    useTableColumns(columnData);
  const {
    reviewedRows,
    visibleRows,
    counts,
    handleApprove,
    handleReset,
    rejectRow,
    approveRemaining,
  } = useReviewState(selectedBatch?.data?.rows);

  const displayedColumns = useMemo(() => {
    return columnOrder
      .filter((key) => visibleColumns.has(key))
      .map((key) => columnData?.columns?.find((col) => col.key === key))
      .filter(Boolean);
  }, [columnOrder, visibleColumns, columnData]);

  const renderCellValue = (row, colKey, isChanged, change) => {
    const currentValue = row.snapshot?.[colKey] ?? "";
    const isTag = isTagKey(colKey, columnData?.tag_columns);
    const chipWasValue = isChanged && change ? change.oldValue : null;

    const displayValue = (v, is_current_value = false) => {
      if (isTag) {
        const tags = parseTagValue(v);
        return tags.length <= 0 ? (
          ""
        ) : is_current_value ? (
          <TagInput value={tags} disabled={true} onChange={() => {}} />
        ) : (
          tags.join(", ")
        );
      }
      return valueToString(v);
    };

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
        {isChanged ? (
          <>
            <Box
              component="span"
              sx={{
                fontSize: "0.8rem",
                color: theme.palette.brain.table.row.text,
              }}
            >
              {displayValue(currentValue, true) || "(empty)"}
            </Box>
            {chipWasValue !== null && (
              <Box
                component="span"
                sx={{
                  fontSize: "0.65rem",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  bgcolor: theme.palette.brain.table.row.editedBackground,
                  border: `1px solid ${theme.palette.brain.table.row.editedBorder}`,
                  color: theme.palette.brain.table.row.editedBorder,
                  width: "fit-content",
                  borderRadius: "5px",
                  px: 0.5,
                  py: 0.1,
                }}
              >
                <Box
                  component="span"
                  sx={{ fontSize: "0.6rem", fontWeight: 700 }}
                >
                  Pred:
                </Box>
                {displayValue(change?.predictedValue) || "(empty)"}
              </Box>
            )}
          </>
        ) : (
          <Box
            component="span"
            sx={{
              color:
                currentValue !== "" && currentValue !== null
                  ? "inherit"
                  : "text.disabled",
            }}
          >
            {displayValue(currentValue, true) || "_"}
          </Box>
        )}
      </Box>
    );
  };
  const handleRejectPrompt = (rowId, prevComment = "") => {
    if (!reviewedRows.some((item) => item.rowId === rowId)) {
      setOpenRejectModal({
        open: true,
        data: { prevComment, rowId, recentComment: "" },
      });
    }
  };
  const handleRejectConfirm = (rowId, recentComment) => {
    rejectRow(rowId, recentComment);
    setOpenRejectModal({ open: false, data: {} });
  };
  const onSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payloadData = {
        submission_status: reviewedRows.find(
          (item) => item.status === "rejected"
        )
          ? "returned_for_review"
          : "approved",
        final_comment: overallReason,
        rows: reviewedRows.filter(
          (item) => !preApprovedRowIds.includes(item.rowId)
        ),
      };
      const response = await submitReviewedSubmission({
        payload: payloadData,
        submission_id: selectedBatch?.data.id,
      });
      if (response.data.status.statusCode === "nam-200") {
        dispatch(
          updateAlertInfo({
            message: "Review submitted successfully!",
            open: true,
            severity: "success",
          })
        );
        navigate("/brain/approver");
      } else {
        dispatch(
          updateAlertInfo({
            message: "Failed to submit Reviewed submission!",
            open: true,
            severity: "error",
          })
        );
      }
    } catch (e) {
      console.error("Error", e);
    }
    setIsSubmitting(false);
  };
  const is_loading =
    selectedBatch?.status === "loading" || selectedBatch?.status === "initial";

  const is_edit_mode =
    (selectedBatch?.data?.assigned_approver || "").toLowerCase() ===
      user_email.toLowerCase() &&
    ["pending", "re-submitted"].includes(selectedBatch.data.status);

  const is_unassigned =
    (selectedBatch?.data?.assigned_approver || "").length === 0 &&
    ["pending", "re-submitted"].includes(selectedBatch.data.status);

  const loadColumnData = async () => {
    try {
      const resp = await getReviewColumns();

      const statusCode = resp.data.status.statusCode;
      if (statusCode === "nam-200") {
        const columns = resp.data.data?.upc_columns;
        const reference_columns = columns
          .filter((item) => !item.is_editable_column)
          .map((item, _) => item.key);
        const restricted_columns = columns
          .filter((item) => item.is_editable_column && !item.user_edit_access)
          .map((item, _) => item.key);
        const editable_columns = columns
          .filter((item) => item.user_edit_access)
          .map((item, _) => item.key);

        dispatch(
          updateReviewColumnData({
            columns: columns,
            key_reference_columns: APP_CONSTANTS.KEY_REFERENCE_COLUMNS,
            reference_columns: reference_columns,
            tag_columns: APP_CONSTANTS.TAG_COLUMNS,
            restricted_columns: restricted_columns,
            editable_columns: editable_columns,
            sku_columns: resp.data.data?.sku_columns,
          })
        );
      }
    } catch (e) {}
  };

  const fetchIndividualSubmissionDataById = async () => {
    try {
      setSelectedBatch({
        status: "loading",
        data: {},
        error: "",
      });
      const response = await fetchIndividualApproverSubmission(submissionId);
      if (response?.data?.status?.statusCode === "nam-200") {
        const data = response?.data?.data;
        const approvedRowIds = data.rows
          .filter((item) => item.status === "approved")
          .map((item, _) => item.rowId);
        setPreApprovedRowIds(approvedRowIds);
        setSelectedBatch({
          status: "success",
          data: data,
          error: "",
        });
      } else {
        setSelectedBatch({
          status: "error",
          data: {},
          error:
            response?.data?.status?.statusMessage || "Internal Server Error",
        });
      }
    } catch (error) {
      setSelectedBatch({
        status: "error",
        data: {},
        error: "Internal Server Error",
      });
    }
  };

  const openSkuForUpc = (upc12, description, row_id) => {
    const sku_rows = selectedBatch.data.upc_sku_map[row_id] || [];
    const sku_columns = columnData.sku_columns;
    setSkuDrawer({
      open: true,
      data: {
        row_id: row_id,
        upc: upc12,
        description: description,
        sku_details: {
          rows: sku_rows,
          columns: sku_columns,
        },
      },
    });
  };

  const handleAssign = async () => {
    setProcessing(true);
    try {
      const response = await assignApprover({ submission_id: submissionId });

      if (response?.data?.status?.statusCode === "nam-200") {
        setSelectedBatch((prev) => {
          return {
            ...prev,
            data: {
              ...prev.data,
              assigned_approver: user_email,
            },
          };
        });
        dispatch(
          updateAlertInfo({
            message: "This submission has been assigned to you for approval.",
            open: true,
            severity: "success",
          })
        );
      } else {
        dispatch(
          updateAlertInfo({
            message:
              "Assignment failed. Refresh and try again — it may already be assigned.",
            open: true,
            severity: "error",
          })
        );
      }
    } catch (error) {
      console.error("error-----> ", error);
    }
    setProcessing(false);
  };

  useEffect(() => {
    if (columnData?.columns?.length === 0) {
      loadColumnData();
    }
    fetchIndividualSubmissionDataById();
  }, []);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        padding: "24px 24px 16px 24px",
      }}
    >
      {is_loading ? (
        <Loader message={"Loading ..."} />
      ) : selectedBatch.error ? (
        <Error
          message={selectedBatch.error || ""}
          fontSize={theme.size[16]}
          margin={theme.margin[1]}
          imageHeight={"30vh"}
          errHeight={"70vh"}
          paddingTop={theme.size[200]}
        />
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "start",
            }}
          >
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Typography
                  variant="h6"
                  component="span"
                  sx={{ fontWeight: 700, color: theme.palette.secondary.main2 }}
                >
                  {!is_edit_mode ? "Preview" : "Review"} Submission #
                  {selectedBatch.data.id}
                </Typography>
                <StatusChip status={selectedBatch.data.status} />
                {!is_edit_mode && (
                  <Tooltip
                    title={
                      ["approved", "returned_for_review"].includes(
                        selectedBatch.data.status
                      )
                        ? `This submission is currently in ${selectedBatch.data.status} state and hence can’t be reviewed.`
                        : is_unassigned
                        ? "Please assign this submission to yourself to start reviewing."
                        : `This submission is assigned to ${selectedBatch.data.assigned_approver} and can’t be reviewed by you.`
                    }
                  >
                    <Chip
                      label={"Read-only"}
                      size="small"
                      variant="outlined"
                      sx={{
                        bgcolor: theme.palette.brain.blue.background,
                        color: theme.palette.brain.blue.text,
                        borderColor: theme.palette.brain.blue.border,
                        textTransform: "capitalize",
                        fontWeight: 500,
                        fontSize: "0.75rem",
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                {selectedBatch.data.total_rows} row
                {selectedBatch.data.total_rows > 1 ? "s" : ""} · submitted by{" "}
                {selectedBatch.data.submitted_by} ·{" "}
                {new Date(selectedBatch.data.submittedAt).toLocaleString()}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <ColumnVisibilityControl
                columns={columnData?.columns || []}
                allowedColumns={columnData?.editable_columns}
                keyRefColumns={columnData?.key_reference_columns}
                refColumns={columnData?.reference_columns}
                restrictedColumns={columnData?.restricted_columns}
                visible={visibleColumns}
                onChange={setVisibleColumns}
                columnOrder={columnOrder}
                onReorder={setColumnOrder}
                theme={theme}
                visibleColumnKey={APP_CONSTANTS.VISIBLE_COLUMN}
                columnOrderKey={APP_CONSTANTS.COLUMN_ORDER}
              />
              {is_edit_mode && (
                <Tooltip title="Approve every pending row in one click. Rejected rows are not affected.">
                  <span>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<DoneAllIcon />}
                      disabled={counts.pending === 0}
                      onClick={approveRemaining}
                      sx={{
                        bgcolor: theme.palette.brain.green.text2,
                        color: "#fff",
                        "&:hover": { bgcolor: theme.palette.brain.green.text3 },
                        "&.Mui-disabled": {
                          bgcolor: theme.palette.brain.grey.disabledBackground,
                          color: theme.palette.brain.grey.text,
                        },
                      }}
                    >
                      Approve remaining
                    </Button>
                  </span>
                </Tooltip>
              )}

              {is_unassigned && (
                <Tooltip title="Approve every pending row in one click. Rejected rows are not affected.">
                  <span>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => {
                        handleAssign();
                      }}
                      startIcon={
                        processing ? (
                          <CircularProgress size={16} />
                        ) : (
                          <PersonAddAlt1Icon size={16} />
                        )
                      }
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        width: "150px",
                        background: theme.palette.brain.green.text,
                        "&:hover": {
                          background: theme.palette.brain.green.text,
                        },
                        color: "#fff",
                      }}
                    >
                      Assign To Me
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>
          </Box>

          {selectedBatch?.data?.comment && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                border: `1px solid ${theme.palette.brain.yellow.border}`,
                bgcolor: theme.palette.brain.yellow.background,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 0.75 }}
              >
                <ChatBubbleOutlineIcon
                  sx={{ fontSize: 16, color: theme.palette.brain.yellow.text }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.brain.yellow.text,
                    textTransform: "uppercase",
                  }}
                >
                  Last review
                </Typography>
              </Stack>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.25,
                  color: theme.palette.brain.table.row.text,
                  whiteSpace: "pre-wrap",
                  fontSize: "0.8rem",
                }}
              >
                {selectedBatch?.data?.comment}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              //   px: 3,
              //   py: 1.25,
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
              borderBottom: `1px solid ${theme.palette.brain.grey.border}`,
              bgcolor: theme.palette.brain.grey.background,
              justifyContent: "space-between",
            }}
          >
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{
                minHeight: 36,
                "& .MuiTab-root": {
                  minHeight: 36,
                  textTransform: "none",
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  px: 1.75,
                  color: theme.palette.brain.table.row.text,
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: theme.palette.secondary.main2,
                  height: 2.5,
                },
              }}
            >
              <Tab label={`All (${counts.all})`} value="all" />
              <Tab label={`Pending (${counts.pending})`} value="pending" />
              <Tab label={`Approved (${counts.approved})`} value="approved" />
              <Tab label={`Rejected (${counts.rejected})`} value="rejected" />
            </Tabs>
            <Stack direction="row" spacing={1} alignItems="center">
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{
                  fontSize: "0.78rem",
                  color: "text.secondary",
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: theme.palette.brain.green.text3,
                    }}
                  />
                  <span>{counts.approved} approved</span>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: theme.palette.brain.red.text,
                    }}
                  />
                  <span>{counts.rejected} rejected</span>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: theme.palette.brain.yellow.text,
                    }}
                  />
                  <span>{counts.pending} pending</span>
                </Box>
              </Stack>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 0.5, mr: 1 }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: 0.5,
                    bgcolor: theme.palette.brain.table.row.editedBackground,
                    border: `1px solid ${theme.palette.brain.table.row.editedBorder}`,
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  Changed
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <LockOutlinedIcon
                  sx={{ fontSize: 14, color: theme.palette.brain.grey.text }}
                />
                <Typography variant="caption" color="text.secondary">
                  Restricted
                </Typography>
              </Box>
            </Stack>
          </Box>

          {visibleRows?.[tab].length === 0 ? (
            <Box
              sx={{
                border: `1px dashed ${theme.palette.brain.grey.border}`,
                borderRadius: 1.5,
                py: 6,
                px: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "text.secondary",
                bgcolor: theme.palette.brain.table.background1,
                flex: 1,
              }}
            >
              <InboxOutlinedIcon
                sx={{
                  fontSize: 40,
                  color: theme.palette.secondary.main2,
                  mb: 1,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.brain.table.header.text,
                }}
              >
                No {tab === "all" ? "" : tab} rows
              </Typography>
            </Box>
          ) : (
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.6,
                  px: 1,
                  marginBottom: "4px",
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 13, color: "#b45309" }} />
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.68rem",
                    fontWeight: 600,
                    color: "#92400e",
                  }}
                >
                  These are AI-generated product attributes. Please review and
                  validate them before approval.
                </Typography>
              </Box>
              <TableContainer
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "auto",
                  border: `1px solid ${theme.palette.brain.mis.grey.border1}`,
                  backgroundColor: theme.palette.brain.table.tableBackground,
                  borderRadius: 2,
                  "&::-webkit-scrollbar": { height: 6, width: 6 },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: theme.palette.brain.mis.grey.background1,
                    borderRadius: 3,
                  },
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        className={classes.tableHeader}
                        sx={{
                          borderRight: `1px solid ${theme.palette.brain.grey.border}`,
                          minWidth: 140,
                          position: "sticky",
                          left: 0,
                          top: 0,
                          zIndex: 4,
                        }}
                      >
                        Row Status
                      </TableCell>
                      {displayedColumns?.map((col) => {
                        const isRestricted = isRestrictedKey(
                          col.key,
                          columnData?.restricted_columns
                        );
                        return (
                          <TableCell
                            key={col.key}
                            className={
                              isRestricted
                                ? classes.restrictedHeader
                                : classes.tableHeader
                            }
                            sx={{
                              minWidth: col.key === "Description" ? 220 : 150,
                              position: "sticky",
                              top: 0,
                              zIndex: 3,
                            }}
                          >
                            <ColumnHeader
                              label={col.label}
                              info={col.info}
                              isRestricted={isRestricted}
                              theme={theme}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleRows?.[tab].map((row, vIdx) => {
                      const rowStatus =
                        reviewedRows.find((item) => item.rowId === row.rowId)
                          ?.status || row.status;
                      const recentComment =
                        reviewedRows.find((item) => item.rowId === row.rowId)
                          ?.comment || null;
                      return (
                        <ReviewTableRow
                          key={row.rowId}
                          row={row}
                          vIdx={vIdx}
                          theme={theme}
                          classes={classes}
                          readOnly={!is_edit_mode}
                          rowStatus={rowStatus}
                          prevRowComment={row.comment || null}
                          recentComment={recentComment}
                          displayedColumns={displayedColumns}
                          columnData={columnData}
                          handleApprove={handleApprove}
                          handleRejectRow={handleRejectPrompt}
                          handleReset={handleReset}
                          renderCellValue={renderCellValue}
                          setOpenRejectModal={setOpenRejectModal}
                          skuCount={
                            (selectedBatch.data.upc_sku_map[row.rowId] || [])
                              .length
                          }
                          skuList={(
                            selectedBatch.data.upc_sku_map?.[row.rowId] || []
                          ).map((item, _) => item.sku)}
                          handleSkuClick={openSkuForUpc}
                          preApprovedRowIds={preApprovedRowIds}
                        />
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {skuDrawer?.open && (
            <SKUDetailsDrawer
              open={skuDrawer?.open}
              data={skuDrawer.data}
              onClose={() => {
                setSkuDrawer({ open: false, data: {} });
              }}
            />
          )}

          <RejectModal
            open={openRejectModal.open || false}
            data={openRejectModal.data}
            theme={theme}
            onClose={() => setOpenRejectModal({ open: false, data: {} })}
            onSave={handleRejectConfirm}
          />

          {
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Button
                onClick={() => navigate("/brain/approver")}
                variant="outlined"
                sx={{
                  textTransform: "none",
                  borderColor: theme.palette.secondary.main2,
                  color: theme.palette.secondary.main2,
                  width: "120px",
                }}
              >
                Back
              </Button>
              {is_edit_mode && (
                <Button
                  onClick={onSubmit}
                  variant="contained"
                  disabled={counts.pending > 0}
                  startIcon={<CheckIcon />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    background: theme.palette.secondary.main2,
                    color: "#fff",
                    "&:hover": { bgcolor: theme.palette.secondary.main2 },
                    "&.Mui-disabled": {
                      bgcolor: theme.palette.brain.grey.disabledBackground,
                      color: theme.palette.brain.grey.text,
                    },
                  }}
                >
                  Submit Review
                </Button>
              )}
            </Box>
          }
        </>
      )}

      {isSubmitting && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 10010,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            bgcolor: theme.palette.brain.mis.grey.disabledBackground,
            backdropFilter: "blur(2px)",
          }}
        >
          <CircularProgress
            size={24}
            sx={{ color: theme.palette.secondary.main2 }}
          />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: theme.palette.brain.table.header.text,
            }}
          >
            Saving changes...
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default ApproverDetailPage;
